import { Button, Card, CardContent, CardHeader, CardTitle, Spinner } from '@1trading/react';
import { ArrowLeft, Download } from 'lucide-react';
import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { ReportCsvViewer } from '@/features/reports/report-csv-viewer';
import { getPresignedUrl } from '@/services/api/reports';
import { openInNewTab } from '@/utils/download';
import { extractFileName } from '@/utils/string';

export interface ReportFileViewerPageProps {
  /** Listing page this viewer was opened from — target of the back affordances. */
  backTo: string;
  backLabel: string;
}

/** CSV report viewer page body, shared by the compliance Reports and operations User Audit routes. */
export const ReportFileViewerPage = ({ backTo, backLabel }: ReportFileViewerPageProps) => {
  const [searchParams] = useSearchParams();
  const fileKey = searchParams.get('key');
  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownload = async (key: string) => {
    setIsDownloading(true);
    try {
      const { url } = await getPresignedUrl(key);

      openInNewTab(url);
    } catch {
      toast.error('Failed to download file');
    } finally {
      setIsDownloading(false);
    }
  };

  if (!fileKey) {
    return (
      <Card>
        <CardContent className="flex flex-col items-start gap-3 pt-6">
          <p className="text-muted-foreground text-sm">No report file selected.</p>
          <Button asChild type="button" variant="outline">
            <Link to={backTo}>
              <ArrowLeft className="size-4" /> {backLabel}
            </Link>
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-3 space-y-0">
        <div className="flex items-center gap-3">
          <Button asChild aria-label={backLabel} size="icon" type="button" variant="ghost">
            <Link to={backTo}>
              <ArrowLeft className="size-4" />
            </Link>
          </Button>
          <CardTitle className="font-mono text-base">{extractFileName(fileKey)}</CardTitle>
        </div>
        <Button className="gap-2" disabled={isDownloading} onClick={() => handleDownload(fileKey)} type="button" variant="outline">
          {isDownloading ? <Spinner className="size-4" /> : <Download className="size-4" />}
          Download
        </Button>
      </CardHeader>
      <CardContent>
        <ReportCsvViewer fileKey={fileKey} />
      </CardContent>
    </Card>
  );
};
ReportFileViewerPage.displayName = 'ReportFileViewerPage';
